import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

/**
 * TaskContext - Manages task modal state and selected task data
 * Provides unified context for ViewTask and AddTask components
 */

const TaskDataContext = createContext(null);
const TaskActionsContext = createContext(null);
const TaskStateContext = createContext(null);

/**
 * Hook to access task data
 * Only rerenders when task data changes
 */
export const useTaskData = () => {
  const context = useContext(TaskDataContext);
  if (!context) {
    throw new Error('useTaskData must be used within TaskProvider');
  }
  return context;
};

/**
 * Hook to access task actions (open/close modals, set task data)
 * Never triggers rerenders (functions are stable)
 */
export const useTaskActions = () => {
  const context = useContext(TaskActionsContext);
  if (!context) {
    throw new Error('useTaskActions must be used within TaskProvider');
  }
  return context;
};

/**
 * Hook to access task state (modal visibility)
 * Only rerenders when state changes
 */
export const useTaskState = () => {
  const context = useContext(TaskStateContext);
  if (!context) {
    throw new Error('useTaskState must be used within TaskProvider');
  }
  return context;
};

/**
 * TaskProvider - Provides task modal state management
 * Manages ViewTask and AddTask modal states and selected task data
 */
export const TaskProvider = ({ children }) => {
  // Modal visibility states
  const [showViewTask, setShowViewTask] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);

  // Selected task for viewing/editing
  const [selectedTask, setSelectedTask] = useState(null);

  // Selected stage for adding new task
  const [selectedStageId, setSelectedStageId] = useState(null);

  // Edit mode flag (for duplicating tasks)
  const [isEditMode, setIsEditMode] = useState(false);

  // Memoize data context value
  const dataContextValue = useMemo(
    () => ({
      selectedTask,
      selectedStageId,
      isEditMode,
    }),
    [selectedTask, selectedStageId, isEditMode]
  );

  // Memoize actions context value - stable functions
  const actionsContextValue = useMemo(
    () => ({
      // View Task actions
      openViewTask: (task) => {
        setSelectedTask(task);
        setShowViewTask(true);
        setIsEditMode(true);
      },
      closeViewTask: () => {
        setShowViewTask(false);
        setSelectedTask(null);
        setIsEditMode(false);
      },

      // Add Task actions
      openAddTask: (stageId = null) => {
        setSelectedStageId(stageId);
        setShowAddTask(true);
        setIsEditMode(false);
      },
      closeAddTask: () => {
        setShowAddTask(false);
        setSelectedStageId(null);
      },

      // Shared actions
      setSelectedTask: (task) => {
        setSelectedTask(task);
      },
      setSelectedStageId: (stageId) => {
        setSelectedStageId(stageId);
      },
      setIsEditMode: (mode) => {
        setIsEditMode(mode);
      },
    }),
    [] // Empty deps - functions are stable
  );

  // Memoize state context value
  const stateContextValue = useMemo(
    () => ({
      showViewTask,
      showAddTask,
      isTaskModalOpen: showViewTask || showAddTask,
    }),
    [showViewTask, showAddTask]
  );

  return (
    <TaskStateContext.Provider value={stateContextValue}>
      <TaskActionsContext.Provider value={actionsContextValue}>
        <TaskDataContext.Provider value={dataContextValue}>{children}</TaskDataContext.Provider>
      </TaskActionsContext.Provider>
    </TaskStateContext.Provider>
  );
};

/**
 * Combined hook for convenience
 * Returns all task context values
 */
export const useTask = () => {
  const data = useTaskData();
  const actions = useTaskActions();
  const state = useTaskState();

  return {
    ...data,
    ...actions,
    ...state,
  };
};
